import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type SortOrder = "asc" | "desc";

const initialState = {
  dealsPortfolioPlans: null as any[] | null,
  isLoadingDealsPortfolioPlans: false,
  lastUpdatedPlanId: null as number | null,
  currencies: null as any[] | null,
  successMessage: null as string | null,
  // Commission Plans table sort + pagination
  commissionPlans: {
    sortBy: "" as string,
    sortOrder: "asc" as SortOrder,
    page: 0,
    rowsPerPage: 10,
    searchQuery: "" as string,
    filters: {
      brand_id: [] as string[],
      website: [] as string[],
      status: [] as string[],
      plan_name: [] as string[],
      plan_term: [] as string[],
    },
  },
};

const dealsPromotionsSlice = createSlice({
  name: "dealsPromotions",
  initialState,
  reducers: {
    setDealsPortfolioPlans: (state, action: PayloadAction<any[]>) => {
      state.dealsPortfolioPlans = action.payload;
    },
    setIsLoadingDealsPortfolioPlans: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDealsPortfolioPlans = action.payload;
    },
    setCommissionPlansSort: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: SortOrder }>,
    ) => {
      state.commissionPlans.sortBy = action.payload.sortBy;
      state.commissionPlans.sortOrder = action.payload.sortOrder;
      state.commissionPlans.page = 0; // reset to first page on sort
    },
    setCommissionPlansPage: (state, action: PayloadAction<number>) => {
      state.commissionPlans.page = action.payload;
    },
    setCommissionPlansRowsPerPage: (state, action: PayloadAction<number>) => {
      state.commissionPlans.rowsPerPage = action.payload;
      state.commissionPlans.page = 0;
    },
    setCommissionPlansSearchQuery: (state, action: PayloadAction<string>) => {
      state.commissionPlans.searchQuery = action.payload;
      state.commissionPlans.page = 0;
    },
    setCurrencies: (state, action: PayloadAction<any[]>) => {
      state.currencies = action.payload;
    },
    setDealsSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    setLastUpdatedPlanId: (state, action: PayloadAction<number | null>) => {
      state.lastUpdatedPlanId = action.payload;
    },
    updateDealsPortfolioPlan: (
      state,
      action: PayloadAction<{ id: number; changes: Record<string, any> }>,
    ) => {
      if (!state.dealsPortfolioPlans) return;
      const idx = state.dealsPortfolioPlans.findIndex(
        (p) => p.id === action.payload.id,
      );
      if (idx !== -1) {
        state.dealsPortfolioPlans[idx] = {
          ...state.dealsPortfolioPlans[idx],
          ...action.payload.changes,
        };
      }
    },
    setCommissionPlansFilter: (
      state,
      action: PayloadAction<{ key: keyof typeof initialState.commissionPlans.filters; value: string[] }>,
    ) => {
      state.commissionPlans.filters[action.payload.key] = action.payload.value;
      state.commissionPlans.page = 0;
    },
    resetDealsPromotions: () => initialState,
  },
});

export const {
  setDealsPortfolioPlans,
  setIsLoadingDealsPortfolioPlans,
  setCurrencies,
  setDealsSuccessMessage,
  setLastUpdatedPlanId,
  updateDealsPortfolioPlan,
  setCommissionPlansSort,
  setCommissionPlansPage,
  setCommissionPlansRowsPerPage,
  setCommissionPlansSearchQuery,
  setCommissionPlansFilter,
  resetDealsPromotions,
} = dealsPromotionsSlice.actions;

export default dealsPromotionsSlice.reducer;
